import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Wrench, Calendar, Clock, Car, User, Phone, MapPin,
  ChevronLeft, ChevronRight, RefreshCw, LogOut, CheckCircle,
  Play, Camera, MessageSquare, AlertCircle, Loader2, Search,
  CalendarDays, List, Grid3X3, Timer, Filter, Bell, Volume2,
  Plus, UserPlus, CreditCard, Banknote, X, Mail, Upload, Film, ClipboardCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { MediaUploadButton, MediaGallery, BulkMediaUpload } from '../components/MediaUpload';
import { VehicleInspectionButton, InspectionViewer } from '../components/VehicleInspection';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'New Booking', color: 'bg-zinc-600', textColor: 'text-zinc-300' },
  checked_in: { label: 'Checked In', color: 'bg-blue-600', textColor: 'text-blue-300' },
  in_progress: { label: 'In Progress', color: 'bg-amber-600', textColor: 'text-amber-300' },
  completed: { label: 'Completed', color: 'bg-green-600', textColor: 'text-green-300' },
  ready_for_pickup: { label: 'Ready', color: 'bg-purple-600', textColor: 'text-purple-300' }
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Jobs' },
  { value: 'pending', label: 'New Bookings' },
  { value: 'checked_in', label: 'Checked In' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Finished' },
  { value: 'ready_for_pickup', label: 'Ready for Pickup' }
];

// Notification sound (base64 encoded short beep)
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp+dlpWMfnBkZG1xeICFiYuNj5KWmJqZlpOQi4Z/d3BpZGBfX2JnaW9zdXl9gYSIi42Pj4+Pj42KhoJ9eHNvbGlnZmVlZmhqbW9xc3Z4e36BhIaIiImJiYiHhYJ/fHl2c3BubGppaGdnaGlrbW9xc3V4en1/gYOFhoaHh4eGhYOBfnx5dnRxb21sa2ppaWlqa2xub3Fyc3V3eXt9f4GDhIWFhYWFhIOBf316eHVzcXBubWxra2trbGxtbnBxc3R2d3l7fH5/gYKDhISEhIOCgX99e3l3dXNxcG9ubWxsbGxtbW5vcHFyc3V2eHl7fH1/gIGCgoODg4KBgH99e3l3dXRycG9ubW1sbGxsbW5ub3BxcnN0dXd4eXt8fX5/gIGBgoKCgoGAgH58e3l4dnVzcnFwb25ubW1tbW5ub3BwcXJzdHV2d3h5ent8fX5/gICBgYGBgIB/fn18e3p4d3Z1dHNycXBvb25ubm5ub29wcHFycnN0dXZ2d3h5ent7fH1+fn9/gICAgIB/f35+fXx7enl4d3Z1dHNycnFxcHBwcHBwcXFxcnJzdHR1dXZ3d3h4eXp6e3t8fH19fn5+f39/f39/f35+fn19fHx7e3p5eXh4d3d2dnV1dXV1dXV1dXZ2dnd3eHh5eXp6e3t7fHx9fX1+fn5/f39/f39/f39+fn59fX19fHx8e3t7enp5eXl5eHh4eHh4eHh4eXl5enp6e3t7fHx8fX19fX5+fn5/f39/f39/f39/f35+fn59fX19fHx8fHt7e3t7e3p6enp6enp6e3t7e3t8fHx8fX19fX5+fn5+f39/f39/f39/f39/f35+fn5+fX19fX19fHx8fHx8fHx8fHx8fHx8fX19fX19fn5+fn5+f39/f39/f39/f39/f39/f39+fn5+fn5+fX19fX19fX19fX19fX19fX19fn5+fn5+fn5/f39/f39/f39/f39/f39/f39/f35+fn5+fn5+fn5+fX19fX19fX19fX19fX5+fn5+fn5+fn9/f39/f39/f39/f39/f39/f39/f35+fn5+fn5+fn5+fn5+fn5+fn5+fn5+fn5/f39/f39/f39/f39/f39/f39/f39/f35+';

export default function WorkshopDashboard() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'list' | 'week' | 'calendar'
  const [weekJobs, setWeekJobs] = useState({});
  const [allJobs, setAllJobs] = useState([]);
  const [calendarJobs, setCalendarJobs] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastJobCount, setLastJobCount] = useState(0);
  const audioRef = useRef(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  });
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    return monday.toISOString().split('T')[0];
  });
  
  // Walk-in booking state
  const [showWalkInModal, setShowWalkInModal] = useState(false);
  const [walkInReceipt, setWalkInReceipt] = useState(null);

  // Initialize audio for notifications
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 0.5;
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, [soundEnabled]);

  // Check authentication
  useEffect(() => {
    checkAuth();
  }, []);

  // Fetch jobs when date changes
  useEffect(() => {
    if (staff) {
      fetchJobs();
      fetchStats();
    }
  }, [staff, selectedDate]);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/api/workshop/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStaff(data);
      } else {
        navigate('/staff/login');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      navigate('/staff/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API}/api/workshop/jobs?date=${selectedDate}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const freshJobs = data.jobs || [];
        setJobs(freshJobs);
        
        // Update selectedJob if it's currently open to reflect new photos/notes
        setSelectedJob(prev => {
          if (prev) {
            const updatedJob = freshJobs.find(j => j.id === prev.id);
            return updatedJob || prev;
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    }
  };

  // Fetch single job (used after photo/note upload to refresh modal)
  const fetchSingleJob = async (jobId) => {
    try {
      const response = await fetch(`${API}/api/workshop/job/${jobId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const freshJob = await response.json();
        // Update the selectedJob with fresh data
        setSelectedJob(freshJob);
        // Also update it in the jobs array
        setJobs(prev => prev.map(j => j.id === jobId ? freshJob : j));
      }
    } catch (error) {
      console.error('Failed to fetch job:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/api/workshop/stats?date=${selectedDate}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchWeekJobs = async () => {
    try {
      const response = await fetch(`${API}/api/workshop/jobs/upcoming?days=7&start_date=${currentWeekStart}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setWeekJobs(data.jobs_by_date || {});
      }
    } catch (error) {
      console.error('Failed to fetch week jobs:', error);
    }
  };

  const fetchAllJobs = async (isAutoRefresh = false) => {
    try {
      const response = await fetch(`${API}/api/workshop/jobs/all?limit=200`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const newJobs = data.jobs || [];
        
        // Check for new bookings (only on auto-refresh, not initial load)
        if (isAutoRefresh && lastJobCount > 0 && newJobs.length > lastJobCount) {
          const newCount = newJobs.length - lastJobCount;
          playNotificationSound();
          toast.success(`${newCount} new booking${newCount > 1 ? 's' : ''} received!`, {
            icon: <Bell className="w-4 h-4" />,
            duration: 5000
          });
        }
        
        setLastJobCount(newJobs.length);
        setAllJobs(newJobs);
      }
    } catch (error) {
      console.error('Failed to fetch all jobs:', error);
    }
  };

  const fetchCalendarJobs = async () => {
    try {
      // Fetch all jobs for calendar grouping
      const response = await fetch(`${API}/api/workshop/jobs/all?limit=500`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCalendarJobs(data.jobs_by_date || {});
      }
    } catch (error) {
      console.error('Failed to fetch calendar jobs:', error);
    }
  };

  // Fetch week jobs when view mode changes to week/month
  useEffect(() => {
    if (staff && viewMode === 'week') {
      fetchWeekJobs();
    }
    if (staff && viewMode === 'all') {
      fetchAllJobs();
    }
    if (staff && viewMode === 'calendar') {
      fetchCalendarJobs();
    }
  }, [staff, viewMode, currentWeekStart, currentMonth]);

  // Auto-refresh every 30 seconds to catch new bookings
  useEffect(() => {
    if (!staff) return;
    
    const interval = setInterval(() => {
      if (viewMode === 'all') {
        fetchAllJobs(true); // Pass true for auto-refresh to enable sound
      } else if (viewMode === 'list') {
        fetchJobs();
      } else if (viewMode === 'calendar') {
        fetchCalendarJobs();
      }
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [staff, viewMode, lastJobCount, playNotificationSound]);

  const handleRefresh = async () => {
    setRefreshing(true);
    if (viewMode === 'list') {
      await Promise.all([fetchJobs(), fetchStats()]);
    } else if (viewMode === 'week') {
      await fetchWeekJobs();
    } else if (viewMode === 'all') {
      await fetchAllJobs();
    } else if (viewMode === 'calendar') {
      await fetchCalendarJobs();
    }
    setRefreshing(false);
    toast.success('Refreshed');
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/workshop/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/staff/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const changeWeek = (weeks) => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + (weeks * 7));
    setCurrentWeekStart(date.toISOString().split('T')[0]);
  };

  const changeMonth = (delta) => {
    setCurrentMonth(prev => {
      let newMonth = prev.month + delta;
      let newYear = prev.year;
      if (newMonth > 11) {
        newMonth = 0;
        newYear++;
      } else if (newMonth < 0) {
        newMonth = 11;
        newYear--;
      }
      return { year: newYear, month: newMonth };
    });
  };

  const getCalendarDays = () => {
    const { year, month } = currentMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 = Sunday
    
    const days = [];
    
    // Add empty slots for days before the 1st
    const startOffset = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // Monday start
    for (let i = 0; i < startOffset; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push(dateStr);
    }
    
    return days;
  };

  const getMonthName = () => {
    const { year, month } = currentMonth;
    return new Date(year, month, 1).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
  };

  const getWeekDays = () => {
    const days = [];
    const start = new Date(currentWeekStart);
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day.toISOString().split('T')[0]);
    }
    return days;
  };

  const selectDateFromCalendar = (date) => {
    setSelectedDate(date);
    setViewMode('list');
  };

  const updateJobStatus = async (jobId, newStatus) => {
    setUpdatingStatus(true);
    try {
      const response = await fetch(`${API}/api/workshop/job/${jobId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
        if (data.notification_sent) {
          toast.success('Customer notified!', { icon: '📱' });
        }
        fetchJobs();
        fetchStats();
        if (selectedJob?.id === jobId) {
          setSelectedJob(prev => ({ ...prev, workshop_status: newStatus }));
        }
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Status update failed:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (dateStr === today.toISOString().split('T')[0]) return 'Today';
    if (dateStr === tomorrow.toISOString().split('T')[0]) return 'Tomorrow';
    
    return date.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="workshop-dashboard">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#C41E3A]/10 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-[#C41E3A]" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Workshop Portal</h1>
                <p className="text-xs text-zinc-500">{staff?.name} • {staff?.role}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowWalkInModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#C41E3A] text-white hover:bg-[#a01830] transition-colors"
                data-testid="add-walkin-btn"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Walk-in</span>
              </button>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-red-400 hover:bg-zinc-700 transition-colors"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* View Mode Toggle */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 bg-zinc-900 rounded-lg p-1">
            <button
              onClick={() => setViewMode('all')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                viewMode === 'all' ? 'bg-[#C41E3A] text-white' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="view-all-btn"
            >
              <Grid3X3 className="w-4 h-4" />
              All Jobs
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                viewMode === 'list' ? 'bg-[#C41E3A] text-white' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="view-day-btn"
            >
              <List className="w-4 h-4" />
              Day
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                viewMode === 'week' ? 'bg-[#C41E3A] text-white' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="view-week-btn"
            >
              <CalendarDays className="w-4 h-4" />
              Week
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                viewMode === 'calendar' ? 'bg-[#C41E3A] text-white' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="view-calendar-btn"
            >
              <Calendar className="w-4 h-4" />
              Calendar
            </button>
          </div>
          
          {/* Today button */}
          <button
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              setSelectedDate(today);
              const dayOfWeek = new Date().getDay();
              const monday = new Date();
              monday.setDate(monday.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
              setCurrentWeekStart(monday.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white text-sm"
          >
            Today
          </button>
        </div>

        {/* Day View Navigation */}
        {viewMode === 'list' && (
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => changeDate(-1)}
              className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-[#C41E3A]" />
              <div className="text-center">
                <h2 className="text-xl font-bold text-white">{formatDate(selectedDate)}</h2>
                <p className="text-xs text-zinc-500">{selectedDate}</p>
              </div>
            </div>
            
            <button
              onClick={() => changeDate(1)}
              className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Week View Navigation */}
        {viewMode === 'week' && (
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => changeWeek(-1)}
              className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-3">
              <CalendarDays className="w-5 h-5 text-[#C41E3A]" />
              <div className="text-center">
                <h2 className="text-xl font-bold text-white">
                  {new Date(currentWeekStart).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' })} - {new Date(new Date(currentWeekStart).getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' })}
                </h2>
              </div>
            </div>
            
            <button
              onClick={() => changeWeek(1)}
              className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Week Calendar View */}
        {viewMode === 'week' && (
          <div className="grid grid-cols-7 gap-2 mb-6">
            {getWeekDays().map((date) => {
              const dayJobs = weekJobs[date] || [];
              const isToday = date === new Date().toISOString().split('T')[0];
              const isSelected = date === selectedDate;
              
              return (
                <div
                  key={date}
                  onClick={() => selectDateFromCalendar(date)}
                  className={`bg-zinc-900 border rounded-xl p-3 cursor-pointer transition-all hover:border-[#C41E3A]/50 ${
                    isToday ? 'border-[#C41E3A]' : isSelected ? 'border-zinc-600' : 'border-zinc-800'
                  }`}
                >
                  <div className="text-center mb-2">
                    <p className="text-xs text-zinc-500">
                      {new Date(date + 'T12:00:00').toLocaleDateString('en-ZA', { weekday: 'short' })}
                    </p>
                    <p className={`text-lg font-bold ${isToday ? 'text-[#C41E3A]' : 'text-white'}`}>
                      {new Date(date + 'T12:00:00').getDate()}
                    </p>
                  </div>
                  
                  {dayJobs.length > 0 ? (
                    <div className="space-y-1">
                      <div className="text-center">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#C41E3A] text-white text-xs font-bold">
                          {dayJobs.length}
                        </span>
                      </div>
                      <div className="flex flex-wrap justify-center gap-1">
                        {dayJobs.slice(0, 3).map((job, i) => (
                          <span
                            key={i}
                            className={`w-2 h-2 rounded-full ${STATUS_CONFIG[job.workshop_status || 'pending']?.color || 'bg-zinc-600'}`}
                          />
                        ))}
                        {dayJobs.length > 3 && (
                          <span className="text-xs text-zinc-500">+{dayJobs.length - 3}</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-600 text-center">No jobs</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* All Jobs View */}
        {viewMode === 'all' && (
          <div className="space-y-4">
            {/* Header with filters */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-white">
                  All Bookings ({statusFilter === 'all' ? allJobs.length : allJobs.filter(j => (j.workshop_status || 'pending') === statusFilter).length})
                </h2>
                <p className="text-xs text-zinc-500">Auto-refreshes every 30 seconds</p>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Sound Toggle */}
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className={`p-2 rounded-lg transition-colors ${
                    soundEnabled ? 'bg-[#C41E3A] text-white' : 'bg-zinc-800 text-zinc-400'
                  }`}
                  title={soundEnabled ? 'Sound alerts ON' : 'Sound alerts OFF'}
                  data-testid="sound-toggle"
                >
                  <Volume2 className="w-4 h-4" />
                </button>
                
                {/* Status Filter */}
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="appearance-none bg-zinc-800 border border-zinc-700 text-white text-sm rounded-lg pl-3 pr-8 py-2 focus:ring-[#C41E3A] focus:border-[#C41E3A]"
                    data-testid="status-filter"
                  >
                    {FILTER_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <Filter className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                </div>
                
                {/* Refresh Button */}
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 text-sm"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>
            
            {/* Filtered and sorted jobs */}
            {(() => {
              // Filter jobs based on status
              const filteredJobs = statusFilter === 'all' 
                ? allJobs 
                : allJobs.filter(j => (j.workshop_status || 'pending') === statusFilter);
              
              // Sort by created_at descending (newest first)
              const sortedJobs = [...filteredJobs].sort((a, b) => {
                const dateA = new Date(a.created_at || 0);
                const dateB = new Date(b.created_at || 0);
                return dateB - dateA;
              });
              
              if (sortedJobs.length === 0) {
                return (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
                    <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-400">
                      {statusFilter === 'all' ? 'No bookings found' : `No ${FILTER_OPTIONS.find(f => f.value === statusFilter)?.label.toLowerCase()} bookings`}
                    </p>
                    <p className="text-xs text-zinc-600 mt-2">New bookings will appear here automatically</p>
                  </div>
                );
              }
              
              return (
                <div className="space-y-3">
                  {sortedJobs.map((job) => (
                    <div 
                      key={job.id}
                      onClick={() => setSelectedJob(job)}
                      className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 cursor-pointer hover:border-zinc-700 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-white">{job.customer_name}</h3>
                          <p className="text-sm text-zinc-400">{job.vehicle_make} {job.vehicle_model}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[job.workshop_status || 'pending']?.color} ${STATUS_CONFIG[job.workshop_status || 'pending']?.textColor}`}>
                          {STATUS_CONFIG[job.workshop_status || 'pending']?.label}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {job.preferred_date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {job.preferred_time}
                        </span>
                        <span className="text-[#C41E3A]">{job.package_name}</span>
                        {job.payment_status === 'paid' && (
                          <span className="text-green-500 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Paid
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-800">
                        {job.reference_number && (
                          <p className="text-xs text-zinc-600">Ref: {job.reference_number}</p>
                        )}
                        {job.created_at && (
                          <p className="text-xs text-zinc-500">
                            Booked: {new Date(job.created_at).toLocaleDateString('en-ZA', { 
                              day: 'numeric', 
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Full Calendar View */}
        {viewMode === 'calendar' && (
          <div className="space-y-4">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => changeMonth(-1)}
                className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <h2 className="text-xl font-bold text-white">{getMonthName()}</h2>
              
              <button
                onClick={() => changeMonth(1)}
                className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            {/* Calendar Grid */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              {/* Day Headers */}
              <div className="grid grid-cols-7 border-b border-zinc-800">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="p-2 text-center text-xs font-medium text-zinc-500 bg-zinc-800/50">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Days */}
              <div className="grid grid-cols-7">
                {getCalendarDays().map((date, index) => {
                  if (!date) {
                    return <div key={`empty-${index}`} className="min-h-[100px] bg-zinc-900/50 border-b border-r border-zinc-800" />;
                  }
                  
                  const dayJobs = calendarJobs[date] || [];
                  const isToday = date === new Date().toISOString().split('T')[0];
                  const dayNum = parseInt(date.split('-')[2]);
                  
                  return (
                    <div
                      key={date}
                      onClick={() => selectDateFromCalendar(date)}
                      className={`min-h-[100px] p-2 border-b border-r border-zinc-800 cursor-pointer hover:bg-zinc-800/50 transition-colors ${
                        isToday ? 'bg-[#C41E3A]/10' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${isToday ? 'text-[#C41E3A]' : 'text-zinc-400'}`}>
                          {dayNum}
                        </span>
                        {dayJobs.length > 0 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#C41E3A] text-white text-xs font-bold">
                            {dayJobs.length}
                          </span>
                        )}
                      </div>
                      
                      {/* Job previews */}
                      <div className="space-y-1">
                        {dayJobs.slice(0, 3).map((job, i) => (
                          <div
                            key={i}
                            className={`text-xs px-1 py-0.5 rounded truncate ${STATUS_CONFIG[job.workshop_status || 'pending']?.color} ${STATUS_CONFIG[job.workshop_status || 'pending']?.textColor}`}
                          >
                            {job.customer_name?.split(' ')[0]}
                          </div>
                        ))}
                        {dayJobs.length > 3 && (
                          <p className="text-xs text-zinc-500">+{dayJobs.length - 3} more</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards - Only show in list view */}
        {viewMode === 'list' && stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <p className="text-2xl font-bold text-white">{stats.total_jobs}</p>
              <p className="text-xs text-zinc-500">Total Jobs</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <p className="text-2xl font-bold text-zinc-400">{stats.pending}</p>
              <p className="text-xs text-zinc-500">Pending</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <p className="text-2xl font-bold text-blue-400">{stats.checked_in}</p>
              <p className="text-xs text-zinc-500">Checked In</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <p className="text-2xl font-bold text-amber-400">{stats.in_progress}</p>
              <p className="text-xs text-zinc-500">In Progress</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <p className="text-2xl font-bold text-green-400">{stats.completed + stats.ready_for_pickup}</p>
              <p className="text-xs text-zinc-500">Completed</p>
            </div>
          </div>
        )}

        {/* Jobs List - Only show in list view */}
        {viewMode === 'list' && (
        <div className="space-y-3">
          {jobs.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
              <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400">No jobs scheduled for this day</p>
            </div>
          ) : (
            jobs.map((job) => (
              <JobCard 
                key={job.id} 
                job={job} 
                onSelect={() => setSelectedJob(job)}
                onStatusChange={updateJobStatus}
                updatingStatus={updatingStatus}
              />
            ))
          )}
        </div>
        )}
      </main>

      {/* Job Detail Modal */}
      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onStatusChange={updateJobStatus}
          updatingStatus={updatingStatus}
          onRefresh={() => fetchSingleJob(selectedJob.id)}
        />
      )}
      
      {/* Walk-in Booking Modal */}
      {showWalkInModal && (
        <WalkInModal
          onClose={() => setShowWalkInModal(false)}
          onSuccess={(receipt) => {
            setShowWalkInModal(false);
            setWalkInReceipt(receipt);
            // Refresh jobs list
            if (viewMode === 'all') fetchAllJobs();
            else fetchJobs();
          }}
        />
      )}
      
      {/* Walk-in Receipt Modal */}
      {walkInReceipt && (
        <WalkInReceipt
          receipt={walkInReceipt}
          onClose={() => setWalkInReceipt(null)}
        />
      )}
    </div>
  );
}

function JobCard({ job, onSelect, onStatusChange, updatingStatus }) {
  const status = STATUS_CONFIG[job.workshop_status] || STATUS_CONFIG.pending;
  const mediaCount = (job.workshop_media?.filter(m => !m.is_deleted)?.length || 0) + (job.workshop_photos?.length || 0);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors cursor-pointer"
      onClick={onSelect}
      data-testid={`job-card-${job.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color} text-white`}>
              {status.label}
            </span>
            <span className="text-xs text-zinc-500">{job.preferred_time}</span>
            {/* Inspection indicator */}
            {job.vehicle_inspection && (
              <span className="px-1.5 py-0.5 bg-green-600/20 text-green-400 rounded text-xs flex items-center gap-1">
                <ClipboardCheck className="w-3 h-3" />
              </span>
            )}
            {/* Media count */}
            {mediaCount > 0 && (
              <span className="px-1.5 py-0.5 bg-zinc-700 text-zinc-300 rounded text-xs flex items-center gap-1">
                <Film className="w-3 h-3" />
                {mediaCount}
              </span>
            )}
          </div>
          
          <h3 className="font-semibold text-white mb-1">
            {job.vehicle_make} {job.vehicle_model}
          </h3>
          <p className="text-sm text-zinc-400 mb-2">{job.package_name || job.package_id}</p>
          
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {job.customer_name}
            </span>
            <span className="flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {job.customer_phone}
            </span>
          </div>
        </div>
        
        <div className="flex flex-col gap-2">
          {job.workshop_status === 'pending' && (
            <button
              onClick={(e) => { e.stopPropagation(); onStatusChange(job.id, 'checked_in'); }}
              disabled={updatingStatus}
              className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Check In
            </button>
          )}
          {job.workshop_status === 'checked_in' && (
            <button
              onClick={(e) => { e.stopPropagation(); onStatusChange(job.id, 'in_progress'); }}
              disabled={updatingStatus}
              className="px-3 py-1.5 bg-amber-600 text-white text-xs rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              Start Job
            </button>
          )}
          {job.workshop_status === 'in_progress' && (
            <button
              onClick={(e) => { e.stopPropagation(); onStatusChange(job.id, 'ready_for_pickup'); }}
              disabled={updatingStatus}
              className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              Complete
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function JobDetailModal({ job, onClose, onStatusChange, updatingStatus, onRefresh }) {
  const [activeTab, setActiveTab] = useState('details');
  const [note, setNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoType, setPhotoType] = useState('before');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showReminder, setShowReminder] = useState(false);
  const status = STATUS_CONFIG[job.workshop_status] || STATUS_CONFIG.pending;

  // Estimated duration in minutes based on package (can be customized)
  const getEstimatedDuration = () => {
    const packageName = (job.package_name || job.package_id || '').toLowerCase();
    if (packageName.includes('prestige') || packageName.includes('full')) return 180; // 3 hours
    if (packageName.includes('executive')) return 120; // 2 hours
    if (packageName.includes('express')) return 60; // 1 hour
    return 90; // default 1.5 hours
  };

  const estimatedMinutes = getEstimatedDuration();

  // Timer effect for in_progress jobs
  useEffect(() => {
    let interval;
    if (job.workshop_status === 'in_progress' && job.job_started_at) {
      const updateTimer = () => {
        const startTime = new Date(job.job_started_at).getTime();
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000 / 60); // minutes
        setElapsedTime(elapsed);
        
        // Show reminder every hour
        if (elapsed > 0 && elapsed % 60 === 0 && elapsed <= estimatedMinutes) {
          setShowReminder(true);
          // Play notification sound or vibrate
          if ('vibrate' in navigator) {
            navigator.vibrate([200, 100, 200]);
          }
        }
      };
      
      updateTimer();
      interval = setInterval(updateTimer, 60000); // Update every minute
    }
    
    return () => clearInterval(interval);
  }, [job.workshop_status, job.job_started_at, estimatedMinutes]);

  const addNote = async () => {
    if (!note.trim()) return;
    setAddingNote(true);
    try {
      const response = await fetch(`${API}/api/workshop/job/${job.id}/note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ note })
      });
      
      if (response.ok) {
        toast.success('Note added');
        setNote('');
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploadingPhoto(true);
    
    try {
      // Compress image before upload
      const compressedBase64 = await compressImage(file, 800, 0.7);
      
      const response = await fetch(`${API}/api/workshop/job/${job.id}/photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          photo_type: photoType,
          photo_url: compressedBase64,
          notes: `${photoType} photo`
        })
      });

      if (response.ok) {
        toast.success(`${photoType} photo uploaded!`);
        onRefresh();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to upload photo');
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      // Reset file input so the same file can be re-selected
      e.target.value = '';
    }
  };

  // Helper function to compress images
  const compressImage = (file, maxWidth, quality) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Scale down if needed
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to compressed JPEG
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        img.onerror = reject;
        img.src = event.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const formatTime = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  const getRemainingTime = () => {
    const remaining = estimatedMinutes - elapsedTime;
    return remaining > 0 ? remaining : 0;
  };

  const getProgressPercent = () => {
    return Math.min((elapsedTime / estimatedMinutes) * 100, 100);
  };

  const isOvertime = elapsedTime > estimatedMinutes;

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-end md:items-center justify-center z-50 p-0 md:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-zinc-900 rounded-t-2xl md:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hourly Reminder Alert */}
        {showReminder && (
          <div className="sticky top-0 z-10 bg-amber-500 text-black p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">Time Check: {formatTime(elapsedTime)} elapsed</span>
            </div>
            <button onClick={() => setShowReminder(false)} className="text-black hover:bg-amber-400 rounded-full p-1">
              ✕
            </button>
          </div>
        )}

        {/* Header with Timer */}
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-lg font-bold text-white">{job.vehicle_make} {job.vehicle_model}</h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.color} text-white`}>
                {status.label}
              </span>
            </div>
            <button onClick={onClose} className="text-zinc-500 hover:text-white text-xl">✕</button>
          </div>
          
          {/* Timer Display for In Progress Jobs */}
          {job.workshop_status === 'in_progress' && (
            <div className="mt-3 bg-zinc-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-500 uppercase">Job Timer</span>
                <span className={`text-xs font-medium ${isOvertime ? 'text-red-400' : 'text-green-400'}`}>
                  {isOvertime ? 'OVERTIME' : 'ON TRACK'}
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="h-2 bg-zinc-700 rounded-full overflow-hidden mb-3">
                <div 
                  className={`h-full transition-all duration-1000 ${isOvertime ? 'bg-red-500' : 'bg-green-500'}`}
                  style={{ width: `${getProgressPercent()}%` }}
                />
              </div>
              
              <div className="grid grid-cols-3 text-center">
                <div>
                  <p className={`text-2xl font-bold ${isOvertime ? 'text-red-400' : 'text-white'}`}>
                    {formatTime(elapsedTime)}
                  </p>
                  <p className="text-xs text-zinc-500">Elapsed</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-400">
                    {formatTime(getRemainingTime())}
                  </p>
                  <p className="text-xs text-zinc-500">Remaining</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-zinc-400">
                    {formatTime(estimatedMinutes)}
                  </p>
                  <p className="text-xs text-zinc-500">Estimated</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-800 overflow-x-auto">
          {['details', 'inspection', 'media', 'notes'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`job-tab-${tab}`}
              className={`flex-1 py-3 text-sm font-medium transition-colors whitespace-nowrap px-2 ${
                activeTab === tab 
                  ? 'text-[#C41E3A] border-b-2 border-[#C41E3A]' 
                  : 'text-zinc-500 hover:text-white'
              }`}
            >
              {tab === 'media' ? (
                <span className="flex items-center justify-center gap-1">
                  <Film className="w-4 h-4" />
                  Media
                  {(job.workshop_media?.filter(m => !m.is_deleted)?.length || 0) + (job.workshop_photos?.length || 0) > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-zinc-700 rounded-full text-xs">
                      {(job.workshop_media?.filter(m => !m.is_deleted)?.length || 0) + (job.workshop_photos?.length || 0)}
                    </span>
                  )}
                </span>
              ) : tab === 'inspection' ? (
                <span className="flex items-center justify-center gap-1">
                  <ClipboardCheck className="w-4 h-4" />
                  Inspection
                  {job.vehicle_inspection && (
                    <span className="ml-1 w-2 h-2 bg-green-500 rounded-full" />
                  )}
                </span>
              ) : (
                tab.charAt(0).toUpperCase() + tab.slice(1)
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {activeTab === 'details' && (
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="bg-zinc-800 rounded-xl p-4">
                <h3 className="text-xs text-zinc-500 mb-3 uppercase">Customer</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-zinc-500" />
                    <span className="text-white">{job.customer_name}</span>
                  </div>
                  <a href={`tel:${job.customer_phone}`} className="flex items-center gap-2 text-[#C41E3A]">
                    <Phone className="w-4 h-4" />
                    {job.customer_phone}
                  </a>
                  {job.customer_email && (
                    <p className="text-sm text-zinc-400">{job.customer_email}</p>
                  )}
                </div>
              </div>

              {/* Service Info */}
              <div className="bg-zinc-800 rounded-xl p-4">
                <h3 className="text-xs text-zinc-500 mb-3 uppercase">Service</h3>
                <p className="text-white font-medium mb-1">{job.package_name || job.package_id}</p>
                <p className="text-sm text-zinc-400">
                  {job.vehicle_colour} {job.vehicle_make} {job.vehicle_model}
                </p>
                <p className="text-xs text-zinc-500 mt-2">Est. Duration: {formatTime(estimatedMinutes)}</p>
                {job.addons?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {job.addons.map((addon, i) => (
                      <span key={i} className="text-xs bg-zinc-700 px-2 py-0.5 rounded text-zinc-300">
                        {addon}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Schedule */}
              <div className="bg-zinc-800 rounded-xl p-4">
                <h3 className="text-xs text-zinc-500 mb-3 uppercase">Schedule</h3>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-zinc-500" />
                  <span className="text-white">{job.preferred_date}</span>
                  <Clock className="w-4 h-4 text-zinc-500 ml-2" />
                  <span className="text-white">{job.preferred_time}</span>
                </div>
              </div>

              {/* Status Action Buttons */}
              <div className="space-y-2">
                {job.workshop_status === 'pending' && (
                  <button
                    onClick={() => onStatusChange(job.id, 'checked_in')}
                    disabled={updatingStatus}
                    className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 text-lg"
                  >
                    <Car className="w-5 h-5" />
                    CHECK IN VEHICLE
                  </button>
                )}
                
                {job.workshop_status === 'checked_in' && (
                  <>
                    <p className="text-xs text-zinc-500 text-center mb-2">
                      Take BEFORE photos before starting the job
                    </p>
                    <button
                      onClick={() => {
                        onStatusChange(job.id, 'in_progress');
                      }}
                      disabled={updatingStatus}
                      className="w-full py-4 bg-amber-500 text-black rounded-xl font-bold hover:bg-amber-400 disabled:opacity-50 flex items-center justify-center gap-2 text-lg"
                    >
                      <Play className="w-6 h-6" />
                      START JOB - BEGIN TIMER
                    </button>
                  </>
                )}
                
                {job.workshop_status === 'in_progress' && (
                  <>
                    <p className="text-xs text-zinc-500 text-center mb-2">
                      Take AFTER photos before completing
                    </p>
                    <button
                      onClick={() => onStatusChange(job.id, 'ready_for_pickup')}
                      disabled={updatingStatus}
                      className="w-full py-4 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 text-lg"
                    >
                      <CheckCircle className="w-6 h-6" />
                      COMPLETE - READY FOR PICKUP
                    </button>
                  </>
                )}
                
                {job.workshop_status === 'ready_for_pickup' && (
                  <div className="text-center py-4">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-green-400 font-semibold">Job Complete!</p>
                    <p className="text-xs text-zinc-500">Customer has been notified</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'inspection' && (
            <div className="space-y-4">
              {/* Inspection Section */}
              <div className="bg-zinc-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs text-zinc-500 uppercase">Vehicle Inspection</h3>
                  <VehicleInspectionButton 
                    bookingId={job.id}
                    existingInspection={job.vehicle_inspection}
                    onComplete={() => onRefresh()}
                  />
                </div>
                
                {job.vehicle_inspection ? (
                  <InspectionViewer inspection={job.vehicle_inspection} />
                ) : (
                  <div className="text-center py-8">
                    <ClipboardCheck className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400 mb-2">No inspection recorded yet</p>
                    <p className="text-xs text-zinc-500">
                      Complete an inspection to document pre-existing damage before starting work
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'media' && (
            <div className="space-y-4">
              {/* HD Media Upload Section */}
              <div className="bg-zinc-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xs text-zinc-500 uppercase mb-1">HD Photos & Videos</h3>
                    <p className="text-xs text-zinc-600">Upload high-resolution images and HD videos up to 1:30</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <BulkMediaUpload 
                      bookingId={job.id}
                      onUploadComplete={() => onRefresh()}
                    />
                    <MediaUploadButton 
                      bookingId={job.id}
                      onUploadComplete={() => onRefresh()}
                    />
                  </div>
                </div>
              </div>

              {/* Media Gallery */}
              <div className="bg-zinc-800 rounded-xl p-4">
                <h3 className="text-xs text-zinc-500 mb-4 uppercase">
                  Media Gallery ({(job.workshop_media?.filter(m => !m.is_deleted)?.length || 0) + (job.workshop_photos?.length || 0)})
                </h3>
                <MediaGallery 
                  media={job.workshop_media || []}
                  photos={job.workshop_photos || []}
                  canDelete={true}
                  onDelete={async (item) => {
                    if (item.source === 'media') {
                      try {
                        const res = await fetch(`${API}/api/workshop/job/${job.id}/media/${item.id}`, {
                          method: 'DELETE',
                          credentials: 'include'
                        });
                        if (res.ok) {
                          toast.success('Media deleted');
                          onRefresh();
                        }
                      } catch (e) {
                        toast.error('Failed to delete');
                      }
                    }
                  }}
                />
              </div>

              {/* Legacy Photo Upload (keeping for backward compatibility) */}
              <div className="bg-zinc-800 rounded-xl p-4">
                <h3 className="text-xs text-zinc-500 mb-3 uppercase">Quick Photo Upload</h3>
                
                {/* Photo Type Selection */}
                <div className="flex gap-2 mb-4">
                  {['before', 'after', 'damage'].map((type) => (
                    <button
                      key={type}
                      onClick={() => setPhotoType(type)}
                      data-testid={`photo-type-${type}`}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        photoType === type
                          ? type === 'damage' ? 'bg-red-600 text-white' : 'bg-[#C41E3A] text-white'
                          : 'bg-zinc-700 text-zinc-400 hover:text-white'
                      }`}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
                
                {/* Upload Button */}
                <label className="block">
                  <div className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                    uploadingPhoto ? 'border-zinc-600 bg-zinc-800' : 'border-zinc-600 hover:border-[#C41E3A] hover:bg-zinc-800/50'
                  }`}>
                    {uploadingPhoto ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="w-5 h-5 text-[#C41E3A] animate-spin" />
                        <p className="text-sm text-zinc-400">Uploading...</p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <Camera className="w-5 h-5 text-zinc-500" />
                        <p className="text-white text-sm">Tap to take {photoType} photo</p>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoUpload}
                    disabled={uploadingPhoto}
                    data-testid="photo-file-input"
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              {/* Add Note */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note..."
                  data-testid="note-input"
                  className="flex-1 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]"
                />
                <button
                  onClick={addNote}
                  data-testid="note-add-btn"
                  disabled={addingNote || !note.trim()}
                  className="px-4 py-2 bg-[#C41E3A] text-white rounded-xl disabled:opacity-50"
                >
                  Add
                </button>
              </div>

              {/* Notes List */}
              {job.workshop_notes?.length > 0 ? (
                <div className="space-y-2">
                  {job.workshop_notes.slice().reverse().map((n, i) => (
                    <div key={i} data-testid={`job-note-${i}`} className="bg-zinc-800 rounded-lg p-3">
                      <p className="text-white text-sm">{n.text}</p>
                      <p className="text-xs text-zinc-500 mt-1">{n.by} • {new Date(n.at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500 text-center py-4">No notes yet</p>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ============ WALK-IN BOOKING MODAL ============
function WalkInModal({ onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1: Customer, 2: Service, 3: Payment
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [packages, setPackages] = useState([]);
  const [addOns, setAddOns] = useState([]);
  
  // Car makes and models data
  const CAR_MAKES = {
    'Alfa Romeo': ['Giulia', 'Stelvio', 'Giulietta', '4C', 'MiTo'],
    'Audi': ['A1', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'Q2', 'Q3', 'Q5', 'Q7', 'Q8', 'RS3', 'RS5', 'RS6', 'RS7', 'S3', 'S4', 'S5', 'TT', 'R8', 'e-tron'],
    'BMW': ['1 Series', '2 Series', '3 Series', '4 Series', '5 Series', '6 Series', '7 Series', '8 Series', 'X1', 'X2', 'X3', 'X4', 'X5', 'X6', 'X7', 'Z4', 'M2', 'M3', 'M4', 'M5', 'M8', 'iX', 'i4', 'i7'],
    'Chevrolet': ['Spark', 'Aveo', 'Cruze', 'Captiva', 'Trailblazer', 'Utility', 'Lumina'],
    'Chery': ['Tiggo 4', 'Tiggo 7', 'Tiggo 8', 'Arrizo 5', 'Arrizo 6'],
    'Citroen': ['C1', 'C3', 'C4', 'C5', 'Berlingo', 'DS3', 'DS4', 'DS5'],
    'Datsun': ['Go', 'Go+', 'Cross'],
    'Fiat': ['500', 'Panda', 'Tipo', 'Doblo', 'Fiorino'],
    'Ford': ['Fiesta', 'Focus', 'EcoSport', 'Puma', 'Kuga', 'Ranger', 'Everest', 'Mustang', 'Figo', 'Tourneo'],
    'GWM': ['P-Series', 'Steed', 'H6', 'H9', 'Tank 300', 'Tank 500'],
    'Haval': ['H1', 'H2', 'H6', 'H9', 'Jolion'],
    'Honda': ['Jazz', 'Civic', 'Accord', 'CR-V', 'HR-V', 'BR-V', 'Fit', 'Ballade', 'Amaze', 'City'],
    'Hyundai': ['i10', 'i20', 'i30', 'Accent', 'Elantra', 'Sonata', 'Venue', 'Creta', 'Tucson', 'Santa Fe', 'Palisade', 'Kona', 'H100', 'Staria'],
    'Isuzu': ['D-Max', 'MU-X', 'KB Series'],
    'JAC': ['T6', 'T8', 'S2', 'S3', 'S4'],
    'Jaguar': ['XE', 'XF', 'XJ', 'E-PACE', 'F-PACE', 'I-PACE', 'F-TYPE'],
    'Jeep': ['Renegade', 'Compass', 'Cherokee', 'Grand Cherokee', 'Wrangler', 'Gladiator'],
    'Kia': ['Picanto', 'Rio', 'Cerato', 'K5', 'Stinger', 'Sonet', 'Seltos', 'Sportage', 'Sorento', 'Carnival', 'EV6'],
    'Land Rover': ['Defender', 'Discovery', 'Discovery Sport', 'Range Rover', 'Range Rover Sport', 'Range Rover Evoque', 'Range Rover Velar'],
    'Lexus': ['IS', 'ES', 'LS', 'NX', 'RX', 'UX', 'GX', 'LX', 'LC', 'RC'],
    'Mahindra': ['XUV300', 'XUV500', 'XUV700', 'Scorpio', 'Pik Up', 'KUV100', 'Thar'],
    'Mazda': ['Mazda2', 'Mazda3', 'Mazda6', 'CX-3', 'CX-30', 'CX-5', 'CX-9', 'BT-50', 'MX-5'],
    'Mercedes-Benz': ['A-Class', 'B-Class', 'C-Class', 'E-Class', 'S-Class', 'CLA', 'CLS', 'GLA', 'GLB', 'GLC', 'GLE', 'GLS', 'G-Class', 'AMG GT', 'EQA', 'EQB', 'EQC', 'EQE', 'EQS', 'V-Class', 'Vito', 'Sprinter'],
    'Mini': ['Cooper', 'Cooper S', 'Countryman', 'Clubman', 'Paceman'],
    'Mitsubishi': ['Mirage', 'ASX', 'Eclipse Cross', 'Outlander', 'Pajero', 'Pajero Sport', 'Triton'],
    'Nissan': ['Micra', 'Almera', 'Qashqai', 'X-Trail', 'Navara', 'NP200', 'NP300', 'Patrol', 'GT-R', 'Leaf', 'Magnite'],
    'Opel': ['Corsa', 'Astra', 'Crossland', 'Grandland', 'Combo', 'Vivaro'],
    'Peugeot': ['108', '208', '308', '2008', '3008', '5008', 'Partner', 'Expert'],
    'Porsche': ['718 Cayman', '718 Boxster', '911', 'Panamera', 'Cayenne', 'Macan', 'Taycan'],
    'Renault': ['Kwid', 'Sandero', 'Clio', 'Megane', 'Captur', 'Duster', 'Kadjar', 'Koleos', 'Triber', 'Kiger'],
    'Suzuki': ['Alto', 'S-Presso', 'Celerio', 'Ignis', 'Swift', 'Baleno', 'Ciaz', 'Vitara Brezza', 'Vitara', 'Jimny', 'Ertiga', 'XL6'],
    'Toyota': ['Aygo', 'Yaris', 'Corolla', 'Camry', 'Starlet', 'Agya', 'Urban Cruiser', 'C-HR', 'Corolla Cross', 'RAV4', 'Fortuner', 'Land Cruiser', 'Hilux', 'Quantum', 'Hiace', 'GR86', 'Supra', 'bZ4X'],
    'Volkswagen': ['Polo', 'Golf', 'T-Cross', 'T-Roc', 'Tiguan', 'Touareg', 'Amarok', 'Caddy', 'Transporter', 'ID.4', 'Arteon', 'Passat', 'Jetta'],
    'Volvo': ['S60', 'S90', 'V40', 'V60', 'V90', 'XC40', 'XC60', 'XC90', 'C40'],
    'Other': ['Other']
  };
  
  const VEHICLE_COLOURS = [
    'White', 'Black', 'Silver', 'Grey', 'Blue', 'Red', 'Green', 
    'Yellow', 'Orange', 'Brown', 'Gold', 'Beige', 'Maroon', 'Navy', 
    'Burgundy', 'Champagne', 'Bronze', 'Pearl White', 'Metallic Grey', 'Other'
  ];
  
  // Form data
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    is_member: false,
    user_id: null,
    vehicle_make: '',
    vehicle_model: '',
    vehicle_type: 'sedan',
    vehicle_colour: '',
    package_id: '',
    add_ons: [],
    payment_method: 'cash',
    amount_paid: 0,
    notes: ''
  });
  
  // Get available models based on selected make
  const availableModels = formData.vehicle_make ? CAR_MAKES[formData.vehicle_make] || [] : [];

  // Fetch packages on mount
  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const [pkgRes, addOnRes] = await Promise.all([
        fetch(`${API}/api/services/packages`),
        fetch(`${API}/api/services/addons`)
      ]);
      if (pkgRes.ok) setPackages(await pkgRes.json());
      if (addOnRes.ok) setAddOns(await addOnRes.json());
    } catch (e) {
      console.error('Failed to fetch packages:', e);
    }
  };

  // Search customers
  const searchCustomers = async (query) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const res = await fetch(`${API}/api/workshop/customer-search?query=${encodeURIComponent(query)}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.customers || []);
      }
    } catch (e) {
      console.error('Search failed:', e);
    } finally {
      setSearching(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) searchCustomers(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const selectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer_name: customer.name,
      customer_phone: customer.phone,
      customer_email: customer.email || '',
      is_member: customer.is_member,
      user_id: customer.user_id
    }));
    setSearchQuery('');
    setSearchResults([]);
  };

  const calculateTotal = () => {
    const pkg = packages.find(p => p.id === formData.package_id);
    if (!pkg) return 0;
    
    let total = pkg.base_price;
    formData.add_ons.forEach(addonId => {
      const addon = addOns.find(a => a.id === addonId);
      if (addon) total += addon.price;
    });
    
    return total;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Clean up data - send null instead of empty strings for optional fields
      const submitData = {
        ...formData,
        customer_email: formData.customer_email?.trim() || null,
        vehicle_colour: formData.vehicle_colour?.trim() || null,
        notes: formData.notes?.trim() || null,
        amount_paid: formData.amount_paid || calculateTotal()
      };
      
      const res = await fetch(`${API}/api/workshop/walk-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(submitData)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Walk-in booking created!');
        onSuccess(data.booking);
      } else {
        const err = await res.json();
        // Handle validation errors which come as array
        const errMsg = typeof err.detail === 'string' 
          ? err.detail 
          : Array.isArray(err.detail) 
            ? err.detail.map(e => e.msg || e.message).join(', ')
            : 'Failed to create booking';
        toast.error(errMsg);
      }
    } catch (e) {
      toast.error('Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const total = calculateTotal();

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-zinc-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="walkin-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#C41E3A]" />
              Walk-in Booking
            </h2>
            <p className="text-xs text-zinc-500">Step {step} of 3</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex px-4 pt-4">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex-1 flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s === step ? 'bg-[#C41E3A] text-white' : 
                s < step ? 'bg-green-600 text-white' : 'bg-zinc-700 text-zinc-400'
              }`}>
                {s < step ? '✓' : s}
              </div>
              {s < 3 && <div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-green-600' : 'bg-zinc-700'}`} />}
            </div>
          ))}
        </div>

        <div className="p-4">
          {/* Step 1: Customer & Vehicle */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Search Existing Customer</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search by phone, email, or name..."
                    className="w-full pl-10 pr-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500"
                    data-testid="customer-search-input"
                  />
                  {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 animate-spin" />}
                </div>
                
                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-2 bg-zinc-800 border border-zinc-700 rounded-xl overflow-hidden">
                    {searchResults.map((c, i) => (
                      <button
                        key={i}
                        onClick={() => selectCustomer(c)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-700 border-b border-zinc-700 last:border-0"
                        data-testid={`customer-result-${i}`}
                      >
                        <div className="text-left">
                          <p className="text-white font-medium">{c.name}</p>
                          <p className="text-xs text-zinc-400">{c.phone} {c.email && `• ${c.email}`}</p>
                        </div>
                        {c.is_member && (
                          <span className="px-2 py-1 bg-green-600/20 text-green-400 text-xs rounded">Member</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-zinc-800 pt-4">
                <p className="text-xs text-zinc-500 mb-3">Or enter new customer details:</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={formData.customer_name}
                      onChange={e => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                      placeholder="Customer Name *"
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500"
                      data-testid="customer-name-input"
                    />
                  </div>
                  <input
                    type="tel"
                    value={formData.customer_phone}
                    onChange={e => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                    placeholder="Phone *"
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500"
                    data-testid="customer-phone-input"
                  />
                  <input
                    type="email"
                    value={formData.customer_email}
                    onChange={e => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                    placeholder="Email (optional)"
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500"
                  />
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-4">
                <p className="text-xs text-zinc-500 mb-3">Vehicle Details:</p>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={formData.vehicle_make}
                    onChange={e => setFormData(prev => ({ 
                      ...prev, 
                      vehicle_make: e.target.value,
                      vehicle_model: '' // Reset model when make changes
                    }))}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white"
                    data-testid="vehicle-make-input"
                  >
                    <option value="">Select Make *</option>
                    {Object.keys(CAR_MAKES).sort().map(make => (
                      <option key={make} value={make}>{make}</option>
                    ))}
                  </select>
                  <select
                    value={formData.vehicle_model}
                    onChange={e => setFormData(prev => ({ ...prev, vehicle_model: e.target.value }))}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white"
                    disabled={!formData.vehicle_make}
                    data-testid="vehicle-model-input"
                  >
                    <option value="">Select Model *</option>
                    {availableModels.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                  <select
                    value={formData.vehicle_type}
                    onChange={e => setFormData(prev => ({ ...prev, vehicle_type: e.target.value }))}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white"
                  >
                    <option value="sedan">Sedan</option>
                    <option value="suv">SUV</option>
                    <option value="hatchback">Hatchback</option>
                    <option value="coupe">Coupe</option>
                    <option value="double_cab">Double Cab</option>
                    <option value="7_seater">7-Seater</option>
                    <option value="minibus">Minibus</option>
                    <option value="van">Van</option>
                  </select>
                  <select
                    value={formData.vehicle_colour}
                    onChange={e => setFormData(prev => ({ ...prev, vehicle_colour: e.target.value }))}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white"
                  >
                    <option value="">Select Colour</option>
                    {VEHICLE_COLOURS.map(colour => (
                      <option key={colour} value={colour}>{colour}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                onClick={() => setStep(2)}
                disabled={!formData.customer_name || !formData.customer_phone || !formData.vehicle_make || !formData.vehicle_model}
                className="w-full py-4 bg-[#C41E3A] text-white rounded-xl font-semibold disabled:opacity-50"
                data-testid="step1-next-btn"
              >
                Next: Select Service
              </button>
            </div>
          )}

          {/* Step 2: Service Selection */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Select Package *</label>
                <div className="space-y-2">
                  {packages.map(pkg => (
                    <button
                      key={pkg.id}
                      onClick={() => setFormData(prev => ({ ...prev, package_id: pkg.id }))}
                      className={`w-full p-4 rounded-xl text-left transition-colors ${
                        formData.package_id === pkg.id
                          ? 'bg-[#C41E3A] border-2 border-[#C41E3A]'
                          : 'bg-zinc-800 border-2 border-zinc-700 hover:border-zinc-600'
                      }`}
                      data-testid={`package-${pkg.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-white">{pkg.name}</p>
                          <p className="text-xs text-zinc-400 mt-1">{pkg.vehicle_category === 'large' ? 'Large Vehicle' : 'Standard'}</p>
                        </div>
                        <p className="font-bold text-white">R{pkg.base_price.toLocaleString()}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {addOns.length > 0 && (
                <div>
                  <label className="block text-xs text-zinc-500 mb-2">Add-ons (optional)</label>
                  <div className="space-y-2">
                    {addOns.map(addon => (
                      <button
                        key={addon.id}
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            add_ons: prev.add_ons.includes(addon.id)
                              ? prev.add_ons.filter(a => a !== addon.id)
                              : [...prev.add_ons, addon.id]
                          }));
                        }}
                        className={`w-full p-3 rounded-xl text-left flex justify-between items-center ${
                          formData.add_ons.includes(addon.id)
                            ? 'bg-zinc-700 border-2 border-[#C41E3A]'
                            : 'bg-zinc-800 border-2 border-zinc-700'
                        }`}
                      >
                        <span className="text-white text-sm">{addon.name}</span>
                        <span className="text-zinc-400 text-sm">+R{addon.price.toLocaleString()}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {formData.package_id && (
                <div className="bg-zinc-800 rounded-xl p-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-zinc-400">Total:</span>
                    <span className="text-white">R{total.toLocaleString()}</span>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-4 bg-zinc-800 text-white rounded-xl font-semibold"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    setFormData(prev => ({ ...prev, amount_paid: total }));
                    setStep(3);
                  }}
                  disabled={!formData.package_id}
                  className="flex-1 py-4 bg-[#C41E3A] text-white rounded-xl font-semibold disabled:opacity-50"
                  data-testid="step2-next-btn"
                >
                  Next: Payment
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-zinc-800 rounded-xl p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Customer:</span>
                  <span className="text-white">{formData.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Vehicle:</span>
                  <span className="text-white">{formData.vehicle_make} {formData.vehicle_model}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Service:</span>
                  <span className="text-white">{packages.find(p => p.id === formData.package_id)?.name}</span>
                </div>
                {formData.add_ons.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Add-ons:</span>
                    <span className="text-white text-right">
                      {formData.add_ons.map(a => addOns.find(ao => ao.id === a)?.name).join(', ')}
                    </span>
                  </div>
                )}
                <div className="border-t border-zinc-700 pt-2 mt-2 flex justify-between text-lg font-bold">
                  <span className="text-zinc-400">Total:</span>
                  <span className="text-white">R{total.toLocaleString()}</span>
                </div>
              </div>

              <div>
                <label className="block text-xs text-zinc-500 mb-2">Payment Method *</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, payment_method: 'cash' }))}
                    className={`p-4 rounded-xl flex flex-col items-center gap-2 ${
                      formData.payment_method === 'cash'
                        ? 'bg-green-600 border-2 border-green-500'
                        : 'bg-zinc-800 border-2 border-zinc-700'
                    }`}
                    data-testid="payment-cash-btn"
                  >
                    <Banknote className="w-8 h-8" />
                    <span className="font-semibold text-white">Cash</span>
                  </button>
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, payment_method: 'card' }))}
                    className={`p-4 rounded-xl flex flex-col items-center gap-2 ${
                      formData.payment_method === 'card'
                        ? 'bg-blue-600 border-2 border-blue-500'
                        : 'bg-zinc-800 border-2 border-zinc-700'
                    }`}
                    data-testid="payment-card-btn"
                  >
                    <CreditCard className="w-8 h-8" />
                    <span className="font-semibold text-white">Card</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs text-zinc-500 mb-2">Amount Paid (R)</label>
                <input
                  type="number"
                  value={formData.amount_paid}
                  onChange={e => setFormData(prev => ({ ...prev, amount_paid: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white text-lg font-bold"
                  data-testid="amount-paid-input"
                />
              </div>

              <div>
                <label className="block text-xs text-zinc-500 mb-2">Notes (optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any special instructions..."
                  rows={2}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 py-4 bg-zinc-800 text-white rounded-xl font-semibold"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !formData.payment_method}
                  className="flex-1 py-4 bg-green-600 text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-walkin-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                  Create Booking
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ============ WALK-IN RECEIPT COMPONENT ============
function WalkInReceipt({ receipt, onClose }) {
  const [sending, setSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailInput, setEmailInput] = useState(receipt.customer_email || '');

  const sendReceiptEmail = async () => {
    if (!emailInput || !emailInput.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSending(true);
    try {
      const res = await fetch(`${API}/api/workshop/send-receipt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          booking_id: receipt.id,
          customer_email: emailInput
        })
      });
      
      if (res.ok) {
        toast.success('Receipt sent! Ask customer to check spam folder if not in inbox.');
        setEmailSent(true);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send email');
      }
    } catch (e) {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-zinc-900 rounded-2xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="walkin-receipt-modal"
      >
        {/* Success Header */}
        <div className="bg-green-600 p-6 text-center">
          <CheckCircle className="w-12 h-12 text-white mx-auto mb-2" />
          <h2 className="text-xl font-bold text-white">Booking Created!</h2>
          <p className="text-green-100 text-sm mt-1">Walk-in successfully added</p>
        </div>

        {/* Receipt Preview */}
        <div className="p-4 space-y-4">
          <div className="bg-zinc-800 rounded-xl p-4 text-center">
            <p className="text-xs text-zinc-500 mb-1">Reference Number</p>
            <p className="text-2xl font-bold text-white font-mono">{receipt.reference_number}</p>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-400">Customer:</span>
              <span className="text-white">{receipt.customer_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Phone:</span>
              <span className="text-white">{receipt.customer_phone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Vehicle:</span>
              <span className="text-white">{receipt.vehicle}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Service:</span>
              <span className="text-white">{receipt.package_name}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-zinc-700">
              <span className="text-zinc-400">Total:</span>
              <span className="text-xl font-bold text-white">R{receipt.total_price.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-zinc-400">Payment:</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                receipt.payment_method === 'CASH' ? 'bg-green-600' : 'bg-blue-600'
              } text-white`}>
                {receipt.payment_method}
              </span>
            </div>
          </div>
          
          {/* Email Input */}
          {!emailSent && (
            <div className="pt-3 border-t border-zinc-800">
              <label className="block text-xs text-zinc-500 mb-2">Send receipt to customer email:</label>
              <input
                type="email"
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                placeholder="customer@email.com"
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500"
                data-testid="receipt-email-input"
              />
            </div>
          )}
          
          {emailSent && (
            <div className="bg-green-600/20 border border-green-600 rounded-xl p-3 text-center">
              <p className="text-green-400 text-sm">✓ Receipt sent to {emailInput}</p>
              <p className="text-yellow-400 text-xs mt-1">💡 Ask customer to check spam folder if not received</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-zinc-800 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-semibold"
          >
            Done
          </button>
          {!emailSent ? (
            <button
              onClick={sendReceiptEmail}
              disabled={sending || !emailInput}
              className="flex-1 py-3 bg-[#C41E3A] text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="send-receipt-btn"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mail className="w-5 h-5" />}
              {sending ? 'Sending...' : 'Email Receipt'}
            </button>
          ) : (
            <button
              onClick={sendReceiptEmail}
              disabled={sending}
              className="flex-1 py-3 bg-zinc-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Resend
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
